<script setup lang="ts">
import { onMounted, ref, nextTick, Ref, onBeforeUnmount, computed } from "vue";
import { useLoading } from "vue-loading-overlay";
import {
  capitalizeFirstLetter,
  getPOSUserFromLocalStorage,
  isAuthPOS,
  logout_pos_user,
  getCookie,
  timePassed,
} from "@/utils/functions";
import { useUserStore } from "@/stores/user.ts";
import { useNotificationStore } from "@/stores/notification";
import Link from "@/views/components/Link.vue";
import { getPOSDetails, getPOSNotifications, changePOSNotificationStatus } from "@/services";
import { ErrorResponse } from "@/utils/enums";
// @ts-ignore
import i18n from "@/localization/i18n";
import { navigate } from "vike/client/router";
import { usePageContext } from "vike-vue/usePageContext";

const pageContext = usePageContext();
const t = i18n.global.t;

const userStore = useUserStore();
const notifStore = useNotificationStore();

const loading = ref(true);

// Notification related variables
const notifications = ref<any[]>([]);
const notifLoading = ref(false);
const $overlayLoading = useLoading({});
let loadingContainer: Ref<any> = ref(null);
const notifPage = ref(1);
const notifContainer: Ref<any> = ref(null);
const dropdown_toggle: Ref<any> = ref(null);
const threshhold = 1;


onMounted(async () => {
  if (!userStore.getPOSUser) {
    userStore.addPOSUser(getPOSUserFromLocalStorage());
  }

  if (isAuthPOS()) {
    if (pageContext.urlPathname == '/partner/login' || pageContext.urlOriginal == "/partner/login/") {
      await navigate('/partner/home/')
    }

    const user = getPOSUserFromLocalStorage();
    let userStore = useUserStore();

    if (user === null) {
      const cookieToken = getCookie("pos_token");

      const accessToken = cookieToken.access;

      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      };

      getPOSDetails(config)
        .then(async (response) => {
          let user_info = response;
          user_info["token"] = cookieToken;

          sessionStorage.setItem("pos_user", JSON.stringify(user_info));
          userStore.addPOSUser(user_info);

          const role = user_info.role
          const path = pageContext.urlPathname
          if (role === ROLE_ADVISOR && (path === '/partner/home' || path === '/partner/home/')) {
            await navigate('/partner/requests/')
          }

          loading.value = false;

          // Fetch notifications after successful POS details retrieval
          fetchNotifications();
        })
        .catch((err: any) => {
          if (
            err.response?.data.code == ErrorResponse.USER_NOT_FOUND ||
            err.response.status === 401
          ) {
            loading.value = false;
            logout_pos_user();
          }
        });
    } else {
      userStore.addPOSUser(user);

      const role = (user as any).role
      const path = pageContext.urlPathname
      if (role === ROLE_ADVISOR && (path === '/partner/home' || path === '/partner/home/')) {
        await navigate('/partner/requests/')
      }

      loading.value = false;
      fetchNotifications();

      console.log("FETCHING SILENTLY:", user);
      // Silently refresh POS user data in the background
      const cookieToken = getCookie("pos_token");
      const refreshConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${cookieToken.access}`,
        },
      };
      getPOSDetails(refreshConfig)
        .then((response) => {
          let user_info = response;
          user_info["token"] = cookieToken;
          sessionStorage.setItem("pos_user", JSON.stringify(user_info));
          userStore.addPOSUser(user_info);
        })
        .catch((err: any) => {
          if (
            err.response?.data.code == ErrorResponse.USER_NOT_FOUND ||
            err.response?.status === 401
          ) {
            logout_pos_user();
          }
        });
    }
  } else {
    console.log("pageContext.urlOriginal", pageContext.urlOriginal)
    if (pageContext.urlOriginal != '/partner/login' && pageContext.urlOriginal != "/partner/login/") {
      await navigate(`/partner/login/?destination=${pageContext.urlOriginal}`);
    }
    loading.value = false;
  }
});

// Attach/detach scroll listener when dropdown is shown/hidden (robust with Bootstrap)
const attachNotifScroll = () => {
  nextTick(() => {
    const menu = document.querySelector(".dropdown-notifications .dropdown-menu");
    const container = notifContainer.value ?? menu?.querySelector(".list-group");
    if (container) {
      container.addEventListener("scroll", handleNotifScroll);
    }
  });
};

const detachNotifScroll = () => {
  const menu = document.querySelector(".dropdown-notifications .dropdown-menu");
  const container = notifContainer.value ?? menu?.querySelector(".list-group");
  if (container) {
    container.removeEventListener("scroll", handleNotifScroll);
  }
};

const onDropdownShown = () => {
  attachNotifScroll();
};

const onDropdownHidden = () => {
  detachNotifScroll();
};

onMounted(() => {
  // Try to get the toggle button (ref) or fallback to selector
  const toggleEl: any = dropdown_toggle.value ?? document.querySelector('.dropdown-notifications .dropdown-toggle');
  if (toggleEl) {
    toggleEl.addEventListener('shown.bs.dropdown', onDropdownShown);
    toggleEl.addEventListener('hidden.bs.dropdown', onDropdownHidden);
  }

  // If dropdown already rendered and container exists, attach immediately
  nextTick(() => {
    const menu = document.querySelector(".dropdown-notifications .dropdown-menu");
    const container = notifContainer.value ?? menu?.querySelector(".list-group");
    if (container) attachNotifScroll();
  });
});

onBeforeUnmount(() => {
  // Cleanup listeners
  const toggleEl: any = dropdown_toggle.value ?? document.querySelector('.dropdown-notifications .dropdown-toggle');
  if (toggleEl) {
    toggleEl.removeEventListener('shown.bs.dropdown', onDropdownShown);
    toggleEl.removeEventListener('hidden.bs.dropdown', onDropdownHidden);
  }
  detachNotifScroll();
});

const ROLE_ADVISOR = 'g_lst_role_request_advisor'
const ROLE_STAFF   = 'g_lst_role_staff'
const ROLE_ADMIN   = 'g_lst_role_admin'

const get_role = (str: string) => {
  if (str === ROLE_ADMIN) {
    return "Responsable Magasin";
  } else if (str === ROLE_STAFF) {
    return "Conseiller Magasin";
  } else if (str === ROLE_ADVISOR) {
    return "Conseiller Pose";
  }
};

const isPageAllowed = (role: string, path: string): boolean => {
  if (!role || role === ROLE_ADMIN) return true
  if (role === ROLE_STAFF) {
    return !path.startsWith('/partner/analytics-dashboard')
  }
  if (role === ROLE_ADVISOR) {
    return path.startsWith('/partner/requests') || path.startsWith('/partner/request-detail')
      || path.startsWith('/partner/externalrequests')
  }
  return true
}

const isRestricted = computed(() => {
  const role = (userStore.getPOSUser as any)?.role
  if (!role) return false
  return !isPageAllowed(role, pageContext.urlPathname)
})

// Notification functions
const fetchNotifications = () => {
  if (notifPage.value !== 0) {
    notifLoading.value = true;
    const container = loadingContainer.value ?? document.querySelector('.dropdown-notifications .dropdown-menu');
    const loader = container ? $overlayLoading.show({ container, opacity: 0.3 }) : null;
    const cookieToken = getCookie("pos_token");
    const payload = {
      pos_id: userStore.getPOSUser?.point_of_sale.id,
      status_list: [
        "g_lst_notif_status_internal_new",
        "g_lst_notif_status_internal_read",
      ],
      type_list: [
        "g_lst_notif_type_info",
        "g_lst_notif_type_warning",
        "g_lst_notif_type_error",
        "g_lst_notif_type_critical",
      ],
    };

    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${cookieToken?.access}`,
      },
      params: {
        page: notifPage.value,
      },
    };

    getPOSNotifications(payload, config)
      .then((response) => {
        notifications.value = notifications.value.concat(response.data.items);
        notifStore.setNotifList(notifications.value);
        notifStore.setCountUnread(response.data.count_unread);
        if (response.data.next) {
          notifPage.value += 1;
        } else {
          notifPage.value = 0;
        }
        notifLoading.value = false;
        try { loader?.hide(); } catch (e) { /* ignore */ }
      })
      .catch((err) => {
        console.error("Error fetching notifications:", err);
        notifLoading.value = false;
        try { loader?.hide(); } catch (e) { /* ignore */ }
      });
  }
};

const handleNotifScroll = () => {
  const container: any = notifContainer.value;

  if (
    container &&
    container.scrollTop + container.clientHeight + threshhold >=
    container.scrollHeight &&
    !notifLoading.value
  ) {
    fetchNotifications();
  }
};

const redirectToLink = async (notif: any) => {
  // Close the dropdown before navigating
  let dropdownElement = document.querySelector(".dropdown-notifications .dropdown-menu");
  if (dropdownElement?.classList.contains("show")) {
    dropdown_toggle.value?.click();
  }

  if (notif.actions && notif.actions.web && notif.actions.web.route) {
    const route = notif.actions.web.route.startsWith('/') ? notif.actions.web.route : `/${notif.actions.web.route}`;
    await navigate(route);
  }
};

const onReadNotification = async (notification: any) => {
  if (notification.internal_status === "g_lst_notif_status_internal_read") {
    return; // Stop if the item is already read
  }

  for (let i = 0; i < notifications.value.length; i++) {
    const item = notifications.value[i];
    if (item.id === notification.id) {
      let read_notification = { ...item }; // Create a copy of the item
      read_notification.internal_status = "g_lst_notif_status_internal_read";
      notifications.value[i] = read_notification; // Update the item in the array
      await nextTick();
      break; // Exit the loop once the item is found and updated
    }
  }

  notifStore.setNotifList(notifications.value);

  if (notifStore.count_un_read > 0) {
    notifStore.setCountUnread(notifStore.count_un_read - 1);
  }

  const cookieToken = getCookie("pos_token");
  let payload = {
    pos_id: 1, // Using pos_id as specified
    status_list: [
      "g_lst_notif_status_internal_new",
      "g_lst_notif_status_internal_read",
    ],
    type_list: [
      "g_lst_notif_type_info",
      "g_lst_notif_type_warning",
      "g_lst_notif_type_error",
      "g_lst_notif_type_critical",
    ],
    target_status: "g_lst_notif_status_internal_read",
  };

  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${cookieToken?.access}`,
    },
    params: {
      id: notification.id,
    },
  };

  changePOSNotificationStatus(payload, config)
    .then((response) => {
      notifStore.setCountUnread(response.data.count_unread ?? notifStore.count_un_read);
    })
    .catch((e) => {console.log(`Error updating notification status: ${e}`)});
};
</script>

<style scoped>
@import "@/assets/css/bootstrap-extended2.css";
@import "@/assets/css/bootstrap-extended.css";
@import "@/assets/css/animate.css";
@import "@/assets/css/main.css";
@import "@/assets/css/responsive.css";

.nav-item.dropdown-notifications .dropdown-toggle::after {
  display: none !important;
}

.dropdown-notifications-list .list-group {
  max-height: 78vh;
  overflow-y: auto;
  overflow-x: hidden;
}

/* Ensure the actual dropdown-menu list-group is scrollable even if Bootstrap moves the menu */
.dropdown-menu .list-group {
  max-height: 78vh;
  overflow-y: auto;
}

.dropdown-notifications-list .list-group::-webkit-scrollbar {
  width: 10px;
}

.dropdown-notifications-list .list-group::-webkit-scrollbar-thumb {
  background-color: #ccc;
  border-radius: 5px;
}

.dropdown-notifications-list .list-group::-webkit-scrollbar-thumb:hover {
  background-color: #aaa;
}

.dropdown-notifications-list .list-group::-webkit-scrollbar-track {
  background-color: #f2f2f2;
}
</style>

<template>
  <template v-if="!loading">
    <div class="partner-layout">
      <header class="partner-header">
        <div class="container px-1 d-flex align-items-center justify-content-between partner-header-inner">
          <div class="d-flex flex-column align-items-center partner-brand">
            <a href="https://www.leroymerlin.fr/" target="_blank"><img
                src="https://upload.wikimedia.org/wikipedia/commons/a/a4/Leroy_Merlin_-_logo_%28France%2C_1995-%29.svg"
                alt="Logo" class="partner-logo" /></a>
            <span class="mb-0 text-center partner-brand-title">{{ t('w_txt_partner_space_title') }} <br />
              <a href="/"><span style="color: #32a5cf">Tope</span><span style="color: #f0a43d">la</span></a></span>
          </div>
          <div class="partner-nav-wrap">
            <nav>
              <ul class="nav">
                <li class="nav-item">
                  <a class="nav-link" href="/partner/home/" :title="t('w_fct_header_home')"><span class="material-symbols-rounded"
                      style="font-size: 36px; vertical-align: middle">
                      home
                    </span></a>
                </li>

                <li class="nav-item dropdown-notifications navbar-dropdown dropdown">
                  <a ref="dropdown_toggle" class="nav-link dropdown-toggle hide-arrow" href="javascript:void(0);" data-bs-toggle="dropdown"
                    data-bs-auto-close="outside" aria-expanded="false" title="Notifications">
                    <span class="material-symbols-rounded" style="font-size: 36px; vertical-align: middle">
                      notifications
                    </span>
                    <span
                      v-if="notifStore.count_un_read && notifStore.count_un_read > 0"
                      class="badge bg-danger rounded-pill badge-notifications"
                      style="position: absolute; top: 10px; right: 3px"
                      >{{ notifStore.count_un_read > 9 ? "9+" : notifStore.count_un_read }}</span
                    >
                  </a>
                  <ul class="dropdown-menu dropdown-menu-end py-0" style="width: 400px">
                    <li class="dropdown-menu-header border-bottom">
                      <div class="dropdown-header d-flex align-items-center py-1">
                        <h5 class="text-body mb-0 me-auto">Notifications</h5>
                        <!-- <a
                          href="javascript:void(0)"
                          class="dropdown-notifications-all text-body"
                          data-bs-toggle="tooltip"
                          data-bs-placement="top"
                          title="Mark all as read"
                          ><span
                            class="material-symbols-rounded me-1"
                            style="
                              font-size: 24px;
                              vertical-align: middle;
                              color: lightgrey;
                            "
                          >
                            mail
                          </span></a> -->

                      </div>
                    </li>
                    <li class="dropdown-notifications-list scrollable-container" style="position:relative;">
                      <ul ref="notifContainer" class="list-group list-group-flush">
                        <li v-if="notifStore.notif_list.length > 0" v-for="(notification, index) in notifStore.notif_list" :key="`${index}`"
                          :class="{
                            'bg-light-warning':
                              notification.internal_status ===
                              'g_lst_notif_status_internal_new',
                          }" class="list-group-item list-group-item-action dropdown-notifications-item">
                          <a class="d-flex" @click.stop.prevent="onReadNotification(notification)" href="javascript:void(0);">
                            <div class="flex-shrink-0 me-1">
                              <div class="avatar" :class="{
                                'bg-info': notification.type === 'g_lst_notif_type_info',
                                'bg-warning': notification.type === 'g_lst_notif_type_warning',
                                'bg-danger': notification.type === 'g_lst_notif_type_error' || notification.type === 'g_lst_notif_type_critical',
                              }">
                                <span class="material-symbols-rounded p-1" style="">
                                  <template v-if="notification.type === 'g_lst_notif_type_info'">info</template>
                                  <template v-else-if="notification.type === 'g_lst_notif_type_warning'">warning</template>
                                  <template v-else>error</template>
                                </span>
                              </div>
                            </div>
                            <div class="flex-grow-1">
                              <h6 class="mb-1" :class="{
                                'fw-bold': notification.internal_status === 'g_lst_notif_status_internal_new',
                              }">
                                {{ notification.notif_contents }}
                              </h6>
                              <div class="d-flex justify-content-between align-items-center">
                                <small class="text-muted">{{ timePassed(notification.posted) }}</small>
                                <button @click.prevent="redirectToLink(notification)" v-if="
                                  notification.actions && notification.actions.web
                                " class="py-0 fw-bold btn btn-light text-center text-nowrap"
                                  style="color: #6e6b7b !important; font-size:12px !important; padding-left: 8px; padding-right: 8px;">
                                  {{ t("w_txt_csHeader_notificationLink") }}
                                </button>
                              </div>
                            </div>
                            <div class="flex-shrink-0 dropdown-notifications-actions">
                              <span class="badge badge-dot"></span>
                            </div>
                          </a>
                        </li>
                        <li v-if="notifStore.notif_list.length === 0" class="list-group-item text-center text-muted p-5">
                          {{ t('w_txt_partner_notifications_empty') }}
                        </li>
                      </ul>
                        <div v-show="notifLoading" ref="loadingContainer" style="
                          height: 100%;
                          width: 100%;
                          position: absolute;
                          top: 0;
                          left: 0;
                          display:flex;
                          align-items:center;
                          justify-content:center;
                        "></div>
                    </li>

                    <!-- <li class="dropdown-menu-footer border-top">
                      <a href="javascript:void(0);"
                        class="dropdown-item d-flex justify-content-center text-primary p-2 h-px-40 mb-1 align-items-center">
                        Voir toutes les notifications
                      </a>
                    </li> -->
                  </ul>
                </li>
                <!-- <li class="nav-item">
                                <a class="nav-link" href="/partner/home/" title="Messagerie"><span
                                        class="material-symbols-rounded "
                                        style="font-size:36px; vertical-align:middle;">
                                        chat
                                    </span></a>
                            </li> -->
                <li class="nav-item navbar-dropdown dropdown-user dropdown">
                  <div class="flex-column d-none d-md-flex">
                    <span class="user-name fw-bolder me-1 text-dark">{{ t('w_txt_partner_greeting') }} {{ userStore.getPOSUser?.first_name
                      }}</span>
                    <small class="text-muted text-dark">{{
                      get_role(userStore.getPOSUser?.role)
                    }}</small>
                  </div>
                  <a class="nav-link dropdown-toggle hide-arrow" href="javascript:void(0);" data-bs-toggle="dropdown">
                    <div class="avatar avatar-online p-1" style="background-color: #7bb51c">
                      {{
                        userStore.getPOSUser?.first_name
                          .toUpperCase()
                          .charAt(0) +
                        userStore.getPOSUser?.last_name?.toUpperCase().charAt(0)
                      }}
                    </div>
                  </a>
                  <ul class="dropdown-menu dropdown-menu-end">
                    <li>
                      <a class="dropdown-item" href="#">
                        <div class="d-flex">
                          <div class="flex-shrink-0 me-1">
                            <div class="avatar avatar-online text-white p-1" style="background-color: #7bb51c">
                              {{
                                userStore.getPOSUser?.first_name
                                  .toUpperCase()
                                  .charAt(0) +
                                userStore.getPOSUser?.last_name
                                  .toUpperCase()
                                  .charAt(0)
                              }}
                            </div>
                          </div>
                          <div class="flex-grow-1">
                            <span class="fw-semibold d-block text-dark">{{
                              capitalizeFirstLetter(
                                userStore.getPOSUser?.first_name,
                              ) +
                              " " +
                              userStore.getPOSUser?.last_name.toUpperCase()
                            }}</span>
                            <small class="text-muted text-dark">{{
                              get_role(userStore.getPOSUser?.role)
                            }}</small>
                          </div>
                        </div>
                      </a>
                    </li>
                    <li>
                      <div class="dropdown-divider"></div>
                    </li>
                    <li>
                      <a class="dropdown-item" href="/partner/pros/">
                        <span class="material-symbols-rounded me-1" style="
                            font-size: 24px;
                            vertical-align: middle;
                            color: lightgrey;
                          ">
                          engineering
                        </span>
                        <span class="align-middle text-dark">{{
                          t("w_fct_partner_profile")
                        }}</span>
                      </a>
                    </li>

                    <li>
                      <a class="dropdown-item" href="/partner/leads/">
                        <span class="d-flex align-items-center align-middle">
                          <span class="material-symbols-rounded me-1" style="
                              font-size: 24px;
                              vertical-align: middle;
                              color: lightgrey;
                            ">
                            groups
                          </span>
                          <span class="flex-grow-1 align-middle text-dark">{{
                            t("w_fct_partner_leads")
                          }}</span>
                          <span
                            class="flex-shrink-0 badge badge-center rounded-pill bg-label-danger w-px-20 h-px-20">2</span>
                        </span>
                      </a>
                    </li>

                    <li>
                      <a class="dropdown-item" href="/partner/requests/">
                        <span class="material-symbols-rounded me-1" style="
                            font-size: 24px;
                            vertical-align: middle;
                            color: lightgrey;
                          ">
                          assignment
                        </span>
                        <span class="align-middle text-dark">{{
                          t("w_fct_partner_requests")
                        }}</span>
                      </a>
                    </li>
                    <li>
                      <div class="dropdown-divider"></div>
                    </li>
                    <li>
                      <Link class="dropdown-item" href="/contact/">
                        <span class="material-symbols-rounded me-1" style="
                            font-size: 24px;
                            vertical-align: middle;
                            color: lightgrey;
                          ">
                          help
                        </span>
                        <span class="align-middle text-dark">{{
                          t("w_fct_partner_help")
                        }}</span>
                      </Link>
                    </li>

                    <li>
                      <button class="dropdown-item" @click="logout_pos_user">
                        <span class="material-symbols-rounded me-1" style="
                            font-size: 24px;
                            vertical-align: middle;
                            color: lightgrey;
                          ">
                          logout
                        </span>
                        <span class="align-middle text-dark">{{
                          t("w_fct_partner_logout")
                        }}</span>
                      </button>
                    </li>
                  </ul>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </header>
      <div class="partner-page">
        <main class="partner-content">
          <template v-if="!loading">
            <div v-if="isRestricted"
                class="content-wrapper container-xxl p-0"
                style="margin-top: 100px">
              <div class="auth-wrapper auth-basic"
                  style="min-height: calc(var(--vh, 1vh) * 80)">
                <div class="w-100 text-center">
                  <span class="material-symbols-rounded"
                      style="font-size: 72px; color: #ccc;">
                    lock
                  </span>
                  <div class="h-style-with-color h3-style use-poppins mt-2">
                    {{ t('w_txt_partner_restricted_title') }}
                  </div>
                  <p class="text-muted mt-1">
                    {{ t('w_txt_partner_restricted_message') }}
                  </p>
                  <a v-if="(userStore.getPOSUser as any)?.role === ROLE_ADVISOR"
                      href="/partner/requests/"
                      class="btn btn-primary mt-3">
                    {{ t('w_act_partner_request_detail_back') }}
                  </a>
                  <a v-else href="/partner/home/" class="btn btn-primary mt-3">
                    {{ t('w_act_partner_restricted_back_home') }}
                  </a>
                </div>
              </div>
            </div>
            <slot v-else />
          </template>
        </main>

        <!-- FOOTER -->
        <footer class="partner-footer">
          <div
            class="container-fluid container-md d-flex flex-column flex-md-row justify-content-between align-items-center gap-2">
            <span class="text-muted text-center text-md-start">
              © {{ new Date().getFullYear() }} Topela · Partenariat Leroy Merlin
            </span>

            <div class="footer-links text-center text-md-end">
              <a href="/legal-notice/" target="_blank" class="me-3">Mentions légales</a>
              <a href="/general-conditions-of-use/" target="_blank" class="me-3">CGU</a>
              <a href="/contact/" target="_blank">Support</a>
            </div>
          </div>
        </footer>
      </div>
    </div>
  </template>
</template>
<style scoped>
li.nav-item {
  display: flex;
  align-items: center;
  margin-left: 16px;
}

/* Layout racine */
.partner-layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* Header fixed */
.partner-header {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  background-color: #fff;
  padding: 16px 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 1000;
}

.partner-page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  padding-top: 110px;
}

.partner-content {
  flex: 1;
  padding: 10px 0px 10px 0px !important;
}

/* Footer */
.partner-footer {
  background-color: #f8f9fa;
  border-top: 1px solid #e5e7eb;
  padding: 16px 0;
  font-size: 14px;
}

/* Liens footer */
.partner-footer a {
  color: #6c757d;
  text-decoration: none;
  transition: color 0.2s ease;
}

.partner-footer a:hover {
  color: #0d6efd;
  text-decoration: underline;
}

.partner-content {
  flex: 1;
  padding: 16px;
}

@media (min-width: 768px) {
  .partner-content {
    padding: 24px;
  }
}

.partner-logo {
  height: 60px;
  /* margin-left: 16px; */
}

@media (max-width: 576px) {
  .partner-logo {
    height: 40px;
    margin-left: 0px;
  }
}

.partner-breadcrumb {
  background-color: #f8f9fa;
  /* gris très clair */
  border-bottom: 1px solid #e5e7eb;
}

/* Mobile header layout — keep desktop untouched */
@media (max-width: 767px) {
  .partner-header {
    padding: 8px 12px;
  }

  /* Existing global .container max-width collapses to 0 at narrow viewports;
     force the header's container to use full width so brand + nav can breathe */
  .partner-header-inner.container {
    max-width: 100% !important;
    width: 100% !important;
  }

  .partner-header-inner {
    gap: 8px;
  }

  .partner-brand {
    flex-direction: row !important;
    align-items: center !important;
    gap: 8px;
    flex-shrink: 1;
    min-width: 0;
  }

  .partner-logo {
    height: 32px;
  }

  .partner-brand-title {
    font-size: 12px;
    line-height: 1.2;
    text-align: left !important;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }

  .partner-nav-wrap {
    flex-shrink: 0;
  }

  .partner-nav-wrap ul.nav {
    flex-wrap: nowrap;
    align-items: center;
  }

  .partner-nav-wrap li.nav-item {
    margin-left: 8px;
  }

  .partner-nav-wrap .nav-link {
    padding: 0.25rem 0.35rem;
  }

  .partner-nav-wrap .material-symbols-rounded {
    font-size: 26px !important;
  }

  .partner-nav-wrap .avatar {
    width: 36px;
    height: 36px;
    font-size: 13px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 !important;
    line-height: 1;
  }

  .partner-page {
    padding-top: 76px;
  }
}

@media (max-width: 400px) {
  .partner-brand-title {
    display: none;
  }
}
</style>
